import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import { productServerFieldError } from '../lib/productServerFieldError';
import { Button, PageHeaderSticky, StickyFooter, toast } from '@/components/ui';
import { useProductQuery } from '../hooks/useProductQuery';
import {
  useDeleteProductMutation,
  useUpdateProductMutation,
} from '../hooks/useProductMutation';
import { useProductEventsQuery } from '../hooks/useProductEventsQuery';
import { useSyncProductEventsMutation } from '../hooks/useProductEventsMutation';
import { useProductCouponsQuery } from '../hooks/useProductCouponsQuery';
import { useSyncProductCouponsMutation } from '../hooks/useSyncProductCouponsMutation';
import {
  useMileageEarnPolicyByProductQuery,
  useSyncProductEarnPolicyMutation,
} from '@/features/mileage/hooks/useMileageEarnPoliciesQuery';
import { ProductForm, type ProductFormSubmit } from '../components/ProductForm';
import { useProductHomeSections } from '../hooks/useProductHomeSections';
import type { EventLinkDraft } from '../components/ProductEventsField';
import type { CouponLinkDraft } from '../components/ProductCouponsField';
import { detailToFormValues, formToUpdateRequest, productAuditTimes, readOptionName } from '../lib/detailMapper';
import {
  useProductCollectionQuery,
  useSaveProductCollectionMutation,
} from '../productCollection/useProductCollection';
import { normalizeCollectionConfig } from '../productCollection/types';
import { COLLECTION_MALLS, collectionSaveFailedMessage, type CollectionMall } from '../productCollection/collectionMalls';
import type { ProductCollectionByMall } from '../productCollection/ProductCollectionMallTabs';
import { emptyCollectionByMall } from '../productCollection/ProductCollectionMallTabs';
import { PreviewUrlButton } from '@/features/cms/preview/PreviewUrlButton';
import { ProductShareUrlButton, type ShareableProduct } from '../components/ProductShareUrlButton';
import { ProductChangeLogButton } from '../components/ProductChangeLogDialog';
import { ProductTextOptionsSection } from '../textOptions/ProductTextOptionsSection';
import {
  optionNamePatchValue,
  usePatchProductOptionNameMutation,
} from '../hooks/useProductOptionNameMutation';

export const ProductEditPage = () => {
  const { t } = useTranslation('products');
  const navigate = useNavigate();
  const params = useParams<{ productId: string }>();
  const productId = params.productId;
  const query = useProductQuery(productId);
  const eventsQuery = useProductEventsQuery(productId);
  const couponsQuery = useProductCouponsQuery(productId);
  const earnPolicyQuery = useMileageEarnPolicyByProductQuery(productId);
  // 항목2 — 4몰 collection-form 을 각각 seed(per-mall GET). 고정 개수라 훅 규칙 충족.
  const collectionKo = useProductCollectionQuery(productId, 'ko-KR');
  const collectionJa = useProductCollectionQuery(productId, 'ja-JP');
  const collectionZh = useProductCollectionQuery(productId, 'zh-CN');
  const collectionEn = useProductCollectionQuery(productId, 'en-US');
  const collectionQueries = [collectionKo, collectionJa, collectionZh, collectionEn];
  const update = useUpdateProductMutation();
  // 저장 거절 원인 칸(예: 비활성 아티스트) — 폼이 그 칸으로 옮겨 간다. 에러가 바뀔 때만 새로 만든다(매 렌더 스크롤 방지).
  const serverFieldError = useMemo(() => productServerFieldError(update.error), [update.error]);
  const remove = useDeleteProductMutation();
  const syncEvents = useSyncProductEventsMutation();
  const syncCoupons = useSyncProductCouponsMutation();
  const syncEarnPolicy = useSyncProductEarnPolicyMutation();
  const saveCollection = useSaveProductCollectionMutation();
  const patchOptionName = usePatchProductOptionNameMutation();
  // QA-463 — 메인 신상품 섹션 노출. 폼은 mount 때 1회 seed 라 섹션 목록도 읽은 뒤 그린다.
  const homeSectionsState = useProductHomeSections(productId ?? null);

  const breadcrumb = [
    { label: t('products.list.title'), href: '/products' },
    { label: t('products.edit.breadcrumbCurrent') },
  ];

  // 이벤트/쿠폰/적립정책 링크는 폼 mount 시 1회만 seed → 모두 settle 후 렌더.
  if (
    query.isPending ||
    (Boolean(productId) &&
      (eventsQuery.isPending ||
        couponsQuery.isPending ||
        earnPolicyQuery.isPending ||
        collectionQueries.some((q) => q.isPending) ||
        homeSectionsState.isPending))
  ) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeaderSticky title={t('products.edit.title')} breadcrumb={breadcrumb} />
        <div
          role="status"
          aria-live="polite"
          className="rounded-xl py-12 text-center text-sm"
          style={{
            background: 'var(--surface-raised)',
            border: '1px solid var(--divider-strong)',
            color: 'var(--content-muted)',
            boxShadow: 'var(--shadow-card-rest)',
          }}
        >
          {t('products.edit.loading')}
        </div>
      </div>
    );
  }

  if (query.isError || !query.data || !productId) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeaderSticky title={t('products.edit.title')} breadcrumb={breadcrumb} />
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-3 rounded-xl py-12 text-center text-sm"
          style={{
            background: 'var(--status-danger-bg)',
            color: 'var(--status-danger-fg)',
          }}
        >
          <p>{t('products.edit.error')}</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/products')}>
            {t('products.actions.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  const defaults = detailToFormValues(query.data);
  const defaultEventLinks: EventLinkDraft[] = (eventsQuery.data ?? []).map((link) => ({
    eventId: link.eventId,
    entryMode: link.entryMode,
    minOrderAmount: link.minOrderAmount,
    minQuantity: link.minQuantity,
    label: link.eventTitle?.trim() || link.eventKey?.trim() || link.eventId,
  }));
  const defaultCouponLinks: CouponLinkDraft[] = (couponsQuery.data ?? []).map((coupon) => ({
    couponId: coupon.id,
    label: coupon.title?.trim() || coupon.badge?.trim() || coupon.code?.trim() || coupon.id,
  }));
  const defaultEarnPolicyId: string | null = earnPolicyQuery.data?.[0]?.id ?? null;
  // 항목2 — 4몰 seed 결과를 맵으로 조립(미로드/404 몰은 빈 세트로 폴백).
  const defaultCollectionByMall: ProductCollectionByMall = {
    ...emptyCollectionByMall(),
    'ko-KR': collectionKo.data ?? { fields: [], consents: [] },
    'ja-JP': collectionJa.data ?? { fields: [], consents: [] },
    'zh-CN': collectionZh.data ?? { fields: [], consents: [] },
    'en-US': collectionEn.data ?? { fields: [], consents: [] },
  };

  const onSubmit = ({ values, eventLinks, couponLinks, earnPolicyId, collectionByMall, homeSections }: ProductFormSubmit) => {
    update.mutate(
      { productId, body: formToUpdateRequest(values, query.data) },
      {
        onSuccess: async () => {
          // 옵션명은 PUT 이 받지 않아 바뀐 경우에만 PATCH 로 보낸다(""= 지움). 실패하면 화면에 남겨 다시 저장하게 한다.
          const optionName = optionNamePatchValue(readOptionName(query.data), values.optionName);
          if (optionName !== undefined) {
            try {
              await patchOptionName.mutateAsync({ productId, optionName });
            } catch {
              return;
            }
          }
          await syncEvents
            .mutateAsync({
              productId,
              links: eventLinks.map((link) => ({
                eventId: link.eventId,
                entryMode: link.entryMode,
                minOrderAmount: link.minOrderAmount ?? null,
                minQuantity: link.minQuantity ?? null,
              })),
            })
            .catch(() => undefined);
          await syncCoupons
            .mutateAsync({
              productId,
              couponDefinitionIds: couponLinks.map((link) => link.couponId),
            })
            .catch(() => undefined);
          await syncEarnPolicy
            .mutateAsync({ productId, policyId: earnPolicyId })
            .catch(() => undefined);
          const homeSectionsFailed = homeSections ? await homeSectionsState.sync(productId, homeSections) : [];
          // 항목2 — 4몰 모두 PUT(전체교체 upsert). 편집에서 비운 몰도 빈 세트로 영속해야
          // 삭제가 반영되므로 빈 몰을 생략하지 않는다. per-mall catch 로 격리.
          const failedMalls: CollectionMall[] = [];
          for (const mall of COLLECTION_MALLS) {
            await saveCollection
              .mutateAsync({
                productId,
                mall,
                config: normalizeCollectionConfig(collectionByMall[mall]),
              })
              .catch(() => failedMalls.push(mall));
          }
          // QA-565 — 추가입력·동의 저장 실패를 "저장되었습니다"로 덮지 않는다. 화면에 남겨 다시 저장하게 한다.
          if (failedMalls.length > 0) {
            toast.error(collectionSaveFailedMessage(failedMalls));
            return;
          }
          // QA-490 — 저장 완료 알림.
          if (homeSectionsFailed.length > 0) {
            toast.error(`메인 섹션 노출을 반영하지 못했습니다(${homeSectionsFailed.join(', ')}). 메인 페이지 관리에서 확인해 주세요.`);
          }
          toast.success(t('products.edit.saved', '저장되었습니다.'));
          navigate('/products');
        },
      },
    );
  };

  const onDelete = () => {
    const confirmed = window.confirm(t('products.edit.confirmDelete'));
    if (!confirmed) return;
    remove.mutate(productId, { onSuccess: () => navigate('/products') });
  };

  const FORM_ID = 'product-edit-form';

  return (
    <div className="flex flex-col gap-6 pb-20">
      <PageHeaderSticky
        title={t('products.edit.title')}
        breadcrumb={breadcrumb}
        primaryActions={
          <div className="flex items-center gap-2">
            {/* ID58: 저장된 상품에 한해 미리보기 URL 생성(active 게이트 우회 검수). */}
            <PreviewUrlButton contentType="PRODUCT" contentId={productId} />
            {/* QA-331: 공개용 상품 단축 URL(만료 없음) — 미리보기와 별개. */}
            <ProductShareUrlButton product={query.data as unknown as ShareableProduct} />
            {/* QA-515 — 언제 누가 무엇을 바꿨는지(고도몰 변경 이력). */}
            <ProductChangeLogButton productId={productId} />
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={onDelete}
              disabled={remove.isPending}
            >
              {t('products.actions.delete')}
            </Button>
          </div>
        }
      />

      {(update.isError || remove.isError || patchOptionName.isError) && (
        <div
          className="rounded-xl px-4 py-3 text-sm"
          role="alert"
          aria-live="polite"
          style={{
            background: 'var(--status-danger-bg)',
            color: 'var(--status-danger-fg)',
            border: '1px solid var(--divider-strong)',
          }}
        >
          {friendlyMessage(update.error ?? remove.error ?? patchOptionName.error, t('products.edit.mutationError'))}
        </div>
      )}

      <ProductForm
        id={FORM_ID}
        editProductId={productId}
        defaultValues={defaults}
        defaultEventLinks={defaultEventLinks}
        defaultCouponLinks={defaultCouponLinks}
        defaultEarnPolicyId={defaultEarnPolicyId}
        homeSectionOptions={homeSectionsState.options}
        defaultHomeSections={homeSectionsState.included}
        defaultCollectionByMall={defaultCollectionByMall}
        auditTimes={productAuditTimes(query.data)}
        serverFieldError={serverFieldError}
        submitting={update.isPending}
        submitLabelKey="products.actions.save"
        onSubmit={onSubmit}
        onCancel={() => navigate('/products')}
        footerSlot={false}
      />

      {/* 텍스트 옵션 — 폼 밖에서 행마다 즉시 저장(상품 저장과 별개, Enter 가 상품 폼을 제출하지 않게). */}
      <ProductTextOptionsSection productId={productId} />

      <StickyFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/products')}
          disabled={update.isPending}
        >
          {t('products.actions.cancel')}
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          variant="primary"
          disabled={update.isPending || patchOptionName.isPending}
        >
          {t('products.actions.save')}
        </Button>
      </StickyFooter>
    </div>
  );
};
